import { Calculate as CalculateIcon, Edit as EditIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useToast } from '../contexts/ToastContext';
import {
  BMR_FORMULA_EQUATIONS,
  BMR_PRESETS,
  BMRCoeffs,
  calculateTargetNutrition,
} from '../utils/bmrFormula';
import { colors } from '../theme';
import CancelButton from './CancelButton';

export type { BMRCoeffs };

interface AddClientModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (clientData: ClientData) => void;
}

interface ClientData {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  age: number;
  weight: number;
  height: number;
  gender: string;
  goal: string;
  activityLevel: string;
  dailyCalories: number;
  dailyProtein: number;
  dailyCarbs: number;
  dailyFat: number;
  notes: string;
  dietitianEmail?: string;
  dietitianName?: string;
}

const AddClientModal: React.FC<AddClientModalProps> = ({
  open,
  onClose,
  onSave,
}) => {
  const { showWarning, showSuccess } = useToast();
  const [formData, setFormData] = useState<ClientData>({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    age: 0,
    weight: 0,
    height: 0,
    gender: '',
    goal: '',
    activityLevel: '',
    dailyCalories: 0,
    dailyProtein: 0,
    dailyCarbs: 0,
    dailyFat: 0,
    notes: '',
    dietitianEmail: localStorage.getItem('userEmail') || '',
    dietitianName: localStorage.getItem('userEmail')?.split('@')[0] || '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  /** BMR formül katsayıları (kilo, boy, yaş çarpanları + sabit; erkek/kadın ayrı) */
  const [bmrCoeffs, setBmrCoeffs] = useState<BMRCoeffs>(() => BMR_PRESETS.mifflin);
  /** Ana formda görünen formül adı (preset veya özel) */
  const [bmrPresetLabel, setBmrPresetLabel] = useState<'mifflin' | 'harris-benedict' | 'harris-benedict-revised' | 'custom'>('mifflin');
  const [formulaEditDialogOpen, setFormulaEditDialogOpen] = useState(false);
  /** Dialog içinde düzenlenen katsayılar (kaydetmeden önce) */
  const [editCoeffs, setEditCoeffs] = useState<BMRCoeffs>(() => BMR_PRESETS.mifflin);
  /** Dialogda seçilen hazır formül (uygulayınca ana formda adı göstermek için) */
  const [selectedPresetInDialog, setSelectedPresetInDialog] = useState<string | null>(null);

  const openFormulaEditDialog = () => {
    setEditCoeffs({ ...bmrCoeffs });
    setSelectedPresetInDialog(null);
    setFormulaEditDialogOpen(true);
  };

  const applyPresetToEdit = (presetKey: string) => {
    const preset = BMR_PRESETS[presetKey];
    if (preset) {
      setEditCoeffs({ ...preset });
      setSelectedPresetInDialog(presetKey);
    }
  };

  const saveFormulaEdit = () => {
    setBmrCoeffs({ ...editCoeffs });
    setBmrPresetLabel(
      selectedPresetInDialog && selectedPresetInDialog in BMR_PRESETS
        ? (selectedPresetInDialog as typeof bmrPresetLabel)
        : 'custom'
    );
    setFormulaEditDialogOpen(false);
  };

  // Otomatik kalori ve makro hesaplama
  const calculateNutrition = () => {
    const { age, weight, height, gender, goal, activityLevel } = formData;
    if (!age || !weight || !height || !gender || !goal || !activityLevel) {
      showWarning(
        'Otomatik hesaplama için yaş, kilo, boy, cinsiyet, hedef ve aktivite seviyesi gereklidir.',
        'Eksik Bilgiler'
      );
      return;
    }
    const result = calculateTargetNutrition(
      { age, weight, height, gender, goal, activityLevel },
      bmrCoeffs
    );
    setFormData((prev) => ({
      ...prev,
      dailyCalories: result.dailyCalories,
      dailyProtein: result.dailyProtein,
      dailyCarbs: result.dailyCarbs,
      dailyFat: result.dailyFat,
    }));
    showSuccess(
      'Günlük kalori ve makro besin değerleri otomatik olarak hesaplandı!',
      'Hesaplama Başarılı'
    );
  };

  const handleInputChange =
    (field: keyof ClientData) =>
    (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any
    ) => {
      let value = event.target.value;

      // Sayısal alanlar için dönüşüm
      if (
        field === 'age' ||
        field === 'weight' ||
        field === 'height' ||
        field === 'dailyCalories' ||
        field === 'dailyProtein' ||
        field === 'dailyCarbs' ||
        field === 'dailyFat'
      ) {
        value = parseFloat(value) || 0;
      }

      setFormData((prev) => ({ ...prev, [field]: value }));

      if (errors[field]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Ad gereklidir';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Soyad gereklidir';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Telefon numarası gereklidir';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'E-posta gereklidir';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Geçerli bir e-posta adresi giriniz';
    }
    if (!formData.age || formData.age <= 0) {
      newErrors.age = "Yaş 0'dan büyük olmalıdır";
    }
    if (!formData.weight || formData.weight <= 0) {
      newErrors.weight = "Kilo 0'dan büyük olmalıdır";
    }
    if (!formData.height || formData.height <= 0) {
      newErrors.height = "Boy 0'dan büyük olmalıdır";
    }
    if (!formData.gender) {
      newErrors.gender = 'Cinsiyet seçiniz';
    }
    if (!formData.goal.trim()) {
      newErrors.goal = 'Hedef belirtiniz';
    }
    if (!formData.activityLevel) {
      newErrors.activityLevel = 'Aktivite seviyesi seçiniz';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      onSave(formData);
      setFormData({
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        age: 0,
        weight: 0,
        height: 0,
        gender: '',
        goal: '',
        activityLevel: '',
        dailyCalories: 0,
        dailyProtein: 0,
        dailyCarbs: 0,
        dailyFat: 0,
        notes: '',
        dietitianEmail: localStorage.getItem('userEmail') || '',
        dietitianName: localStorage.getItem('userEmail')?.split('@')[0] || '',
      });
      setErrors({});
      onClose();
    } else {
      showWarning(
        'Lütfen tüm zorunlu alanları doğru şekilde doldurun.',
        'Eksik Bilgiler'
      );
    }
  };

  const handleClose = () => {
    setFormData({
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      age: 0,
      weight: 0,
      height: 0,
      gender: '',
      goal: '',
      activityLevel: '',
      dailyCalories: 0,
      dailyProtein: 0,
      dailyCarbs: 0,
      dailyFat: 0,
      notes: '',
    });
    setErrors({});
    onClose();
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1.5,
          pt: 2,
          px: 2,
          background: 'linear-gradient(135deg, #43A047 0%, #2E7D32 100%)',
          color: 'white',
        }}
      >
        <Typography variant="h6" component="div" fontWeight={700}>
          Yeni Danışan Ekle
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ bgcolor: '#fafbfc', p: 1.5, pt: 1 }}>
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: 'white',
          }}
        >
          {/* Kişisel Bilgiler */}
          <Typography variant="subtitle2" fontWeight={600} color="#43A047" mb={1}>
            Kişisel Bilgiler
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: 1.5,
              mb: 1.5,
            }}
          >
            <Box>
              <TextField
                fullWidth
                size="small"
                label="Ad"
                value={formData.firstName}
                onChange={handleInputChange('firstName')}
                error={!!errors.firstName}
                helperText={errors.firstName}
                required
              />
            </Box>
            <Box>
              <TextField
                fullWidth
                label="Soyad"
                value={formData.lastName}
                onChange={handleInputChange('lastName')}
                error={!!errors.lastName}
                helperText={errors.lastName}
                required
              />
            </Box>
            <Box>
              <TextField
                fullWidth
                size="small"
                label="Telefon"
                value={formData.phone}
                onChange={handleInputChange('phone')}
                error={!!errors.phone}
                helperText={errors.phone}
                required
              />
            </Box>
            <TextField
              fullWidth
              size="small"
              label="E-posta"
              type="email"
              value={formData.email}
              onChange={handleInputChange('email')}
              error={!!errors.email}
              helperText={errors.email}
              required
            />
            <TextField
              fullWidth
              size="small"
              label="Yaş"
              type="number"
              value={formData.age || ''}
              onChange={handleInputChange('age')}
              error={!!errors.age}
              helperText={errors.age}
              required
              inputProps={{ min: 1, max: 120 }}
            />
            <FormControl fullWidth size="small" required error={!!errors.gender}>
              <InputLabel>Cinsiyet</InputLabel>
              <Select
                value={formData.gender}
                onChange={handleInputChange('gender')}
                label="Cinsiyet"
              >
                <MenuItem value="erkek">Erkek</MenuItem>
                <MenuItem value="kadın">Kadın</MenuItem>
              </Select>
              {errors.gender && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, ml: 1.5 }}
                >
                  {errors.gender}
                </Typography>
              )}
            </FormControl>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          {/* Fiziksel Ölçümler */}
          <Typography variant="subtitle2" fontWeight={600} color="#43A047" mb={1}>
            Fiziksel Ölçümler
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 1.5,
              mb: 1.5,
            }}
          >
            <TextField
              fullWidth
              size="small"
              label="Kilo"
              type="number"
              value={formData.weight || ''}
              onChange={handleInputChange('weight')}
              error={!!errors.weight}
              helperText={errors.weight}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">kg</InputAdornment>
                ),
              }}
              inputProps={{ min: 1, max: 300, step: 0.1 }}
            />
            <TextField
              fullWidth
              size="small"
              label="Boy"
              type="number"
              value={formData.height || ''}
              onChange={handleInputChange('height')}
              error={!!errors.height}
              helperText={errors.height}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">cm</InputAdornment>
                ),
              }}
              inputProps={{ min: 50, max: 250 }}
            />
          </Box>

          <Divider sx={{ my: 1.5 }} />

          {/* Hedef ve Aktivite */}
          <Typography variant="subtitle2" fontWeight={600} color="#43A047" mb={1}>
            Hedef ve Aktivite Seviyesi
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 1.5,
              mb: 1.5,
            }}
          >
            <FormControl fullWidth size="small" required error={!!errors.goal}>
              <InputLabel>Hedef</InputLabel>
              <Select
                value={formData.goal}
                onChange={handleInputChange('goal')}
                label="Hedef"
              >
                <MenuItem value="Kilo verme">Kilo Verme</MenuItem>
                <MenuItem value="Kilo alma">Kilo Alma</MenuItem>
                <MenuItem value="Kas kazanma">Kas Kazanma</MenuItem>
                <MenuItem value="Kilo koruma">Kilo Koruma</MenuItem>
                <MenuItem value="Sağlıklı beslenme">Sağlıklı Beslenme</MenuItem>
              </Select>
              {errors.goal && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, ml: 1.5 }}
                >
                  {errors.goal}
                </Typography>
              )}
            </FormControl>
            <FormControl fullWidth size="small" required error={!!errors.activityLevel}>
              <InputLabel>Aktivite Seviyesi</InputLabel>
              <Select
                value={formData.activityLevel}
                onChange={handleInputChange('activityLevel')}
                label="Aktivite Seviyesi"
              >
                <MenuItem value="sedanter">Hareketsiz (Egzersiz yok)</MenuItem>
                <MenuItem value="hafif">Hafif Aktif (Haftada 1-3 gün)</MenuItem>
                <MenuItem value="orta">Orta Aktif (Haftada 3-5 gün)</MenuItem>
                <MenuItem value="agir">Çok Aktif (Haftada 6-7 gün)</MenuItem>
                <MenuItem value="profesyonel">Profesyonel Sporcu</MenuItem>
              </Select>
              {errors.activityLevel && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, ml: 1.5 }}
                >
                  {errors.activityLevel}
                </Typography>
              )}
            </FormControl>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          {/* Kalori ve Makro Hesaplama — BMR formülü + Otomatik Hesapla */}
          <Paper
            elevation={0}
            sx={{
              overflow: 'hidden',
              mb: 1.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            }}
          >
            <Box
              sx={{
                px: 1.5,
                py: 1,
                bgcolor: 'rgba(255, 152, 0, 0.08)',
                borderLeft: '4px solid',
                borderLeftColor: '#FF9800',
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              <Typography variant="subtitle2" fontWeight={700} color="#E65100">
                Kalori ve Makro Hesaplama
              </Typography>
            </Box>
            <Stack spacing={1.5} sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
                  BMR formülü:
                </Typography>
                <Typography variant="body1" fontWeight={600} color="text.primary">
                  {bmrPresetLabel === 'mifflin' && 'Mifflin-St Jeor (1990)'}
                  {bmrPresetLabel === 'harris-benedict' && 'Harris-Benedict Orijinal (1919)'}
                  {bmrPresetLabel === 'harris-benedict-revised' && 'Harris-Benedict Revize (1984)'}
                  {bmrPresetLabel === 'custom' && 'Özel formül'}
                </Typography>
                <Tooltip title="Formül katsayılarını düzenle (çarpılan / toplanan değerler)">
                  <IconButton
                    size="small"
                    onClick={openFormulaEditDialog}
                    sx={{
                      color: '#FF9800',
                      border: '1px solid',
                      width: 'fit-content',
                      borderColor: 'rgba(255, 152, 0, 0.5)',
                      bgcolor: 'rgba(255, 152, 0, 0.06)',
                      '&:hover': {
                        bgcolor: 'rgba(255, 152, 0, 0.12)',
                        borderColor: '#FF9800',
                      },
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box sx={{ textAlign: 'center', pt: 0 }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<CalculateIcon />}
                  onClick={calculateNutrition}
                  sx={{
                    bgcolor: '#FF9800',
                    color: 'white',
                    fontWeight: 600,
                    py: 1,
                    px: 2.5,
                    borderRadius: 1.5,
                    boxShadow: '0 2px 10px rgba(255, 152, 0, 0.3)',
                    '&:hover': { bgcolor: '#F57C00', boxShadow: '0 4px 12px rgba(255, 152, 0, 0.4)' },
                  }}
                >
                  Otomatik Hesapla
                </Button>
                <Typography variant="caption" display="block" color="text.secondary" mt={0.75}>
                  Günlük kalori ve makro formüle göre hesaplanacak.
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {/* Makro Besinler */}
          <Typography variant="subtitle2" fontWeight={600} color="#43A047" mb={1}>
            Günlük Hedefler (Manuel Düzenlenebilir)
          </Typography>
          <Paper sx={{ p: 1.5, bgcolor: '#f5f7fa', mb: 1.5 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                gap: 1.5,
              }}
            >
              <TextField
                fullWidth
                size="small"
                label="Kalori"
                type="number"
                value={formData.dailyCalories || ''}
                onChange={handleInputChange('dailyCalories')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">kcal</InputAdornment>
                  ),
                }}
                inputProps={{ min: 0, step: 50 }}
              />
              <TextField
                fullWidth
                size="small"
                label="Protein"
                type="number"
                value={formData.dailyProtein || ''}
                onChange={handleInputChange('dailyProtein')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">g</InputAdornment>
                  ),
                }}
                inputProps={{ min: 0, step: 5 }}
              />
              <TextField
                fullWidth
                size="small"
                label="Karbonhidrat"
                type="number"
                value={formData.dailyCarbs || ''}
                onChange={handleInputChange('dailyCarbs')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">g</InputAdornment>
                  ),
                }}
                inputProps={{ min: 0, step: 5 }}
              />
              <TextField
                fullWidth
                size="small"
                label="Yağ"
                type="number"
                value={formData.dailyFat || ''}
                onChange={handleInputChange('dailyFat')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">g</InputAdornment>
                  ),
                }}
                inputProps={{ min: 0, step: 5 }}
              />
            </Box>
          </Paper>

          {/* Notlar */}
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              borderRadius: 2,
              border: `1px solid ${colors.border?.light || '#e0e0e0'}`,
              bgcolor: 'white',
              boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
              mt: 1.5,
            }}
          >
            <Typography variant="subtitle2" fontWeight={600} color={colors.text.primary} mb={1}>
              Ek Notlar
            </Typography>
            <TextField
              fullWidth
              size="small"
              label="Notlar"
              value={formData.notes}
              onChange={handleInputChange('notes')}
              multiline
              rows={2}
              placeholder="Danışan hakkında ek notlar, alerjiler, özel durumlar..."
            />
          </Paper>
        </Paper>
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          pt: 1.5,
          bgcolor: '#fafbfc',
          borderTop: '1px solid',
          borderColor: 'divider',
          gap: 1,
        }}
      >
        <CancelButton onClick={handleClose} />
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{
            bgcolor: colors.primary.main,
            color: 'white',
            textTransform: 'none',
            px: 4,
            py: 1,
            borderRadius: 1.5,
            boxShadow: '0 4px 12px rgba(67,160,71,0.3)',
            '&:hover': {
              bgcolor: colors.hover.primary,
              boxShadow: '0 6px 16px rgba(67,160,71,0.4)',
            },
          }}
        >
          Kaydet
        </Button>
      </DialogActions>
    </Dialog>

    {/* BMR formül katsayılarını düzenleme dialogu */}
    <Dialog
      open={formulaEditDialogOpen}
      onClose={() => setFormulaEditDialogOpen(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider', py: 1.5, px: 2 }}>
        BMR formülünü düzenle
      </DialogTitle>
      <DialogContent sx={{ pt: 1.5, px: 2 }}>
      
        <FormControl fullWidth size="small" sx={{ mb: 1.5 ,mt: 1.5}}>
          <InputLabel>Hazır formül yükle</InputLabel>
          <Select
            label="Hazır formül yükle"
            value={selectedPresetInDialog ?? ''}
            onChange={(e) => applyPresetToEdit(e.target.value)}
          >
            <MenuItem value="">Seçiniz veya aşağıdan katsayıları düzenleyin</MenuItem>
            <MenuItem value="mifflin">Mifflin-St Jeor (1990)</MenuItem>
            <MenuItem value="harris-benedict">Harris-Benedict Orijinal (1919)</MenuItem>
            <MenuItem value="harris-benedict-revised">Harris-Benedict Revize (1984)</MenuItem>
          </Select>
        </FormControl>
        {selectedPresetInDialog && BMR_FORMULA_EQUATIONS[selectedPresetInDialog] && (
          <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" gutterBottom>
              Denklem (bilgi):
            </Typography>
            <Typography variant="caption" component="div" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
              Erkek: {BMR_FORMULA_EQUATIONS[selectedPresetInDialog].erkek}
            </Typography>
            <Typography variant="caption" component="div" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', mt: 0.5 }}>
              Kadın: {BMR_FORMULA_EQUATIONS[selectedPresetInDialog].kadın}
            </Typography>
          </Paper>
        )}
        <Stack spacing={1.5}>
          <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'rgba(33, 150, 243, 0.04)' }}>
            <Typography variant="caption" fontWeight={600} color="primary" gutterBottom display="block">Erkek</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
              <TextField
                size="small"
                label="Kilo katsayısı"
                type="number"
                value={editCoeffs.weightCoefMale}
                onChange={(e) => { setSelectedPresetInDialog(null); setEditCoeffs((c) => ({ ...c, weightCoefMale: parseFloat(e.target.value) || 0 })); }}
                inputProps={{ step: 0.01 }}
              />
              <TextField
                size="small"
                label="Boy katsayısı"
                type="number"
                value={editCoeffs.heightCoefMale}
                onChange={(e) => { setSelectedPresetInDialog(null); setEditCoeffs((c) => ({ ...c, heightCoefMale: parseFloat(e.target.value) || 0 })); }}
                inputProps={{ step: 0.01 }}
              />
              <TextField
                size="small"
                label="Yaş katsayısı"
                type="number"
                value={editCoeffs.ageCoefMale}
                onChange={(e) => { setSelectedPresetInDialog(null); setEditCoeffs((c) => ({ ...c, ageCoefMale: parseFloat(e.target.value) || 0 })); }}
                inputProps={{ step: 0.01 }}
              />
              <TextField
                size="small"
                label="Sabit"
                type="number"
                value={editCoeffs.constantMale}
                onChange={(e) => { setSelectedPresetInDialog(null); setEditCoeffs((c) => ({ ...c, constantMale: parseFloat(e.target.value) || 0 })); }}
                inputProps={{ step: 0.01 }}
              />
            </Box>
          </Paper>
          <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'rgba(233, 30, 99, 0.04)' }}>
            <Typography variant="caption" fontWeight={600} sx={{ color: '#E91E63' }} gutterBottom display="block">Kadın</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
              <TextField
                size="small"
                label="Kilo katsayısı"
                type="number"
                value={editCoeffs.weightCoefFemale}
                onChange={(e) => { setSelectedPresetInDialog(null); setEditCoeffs((c) => ({ ...c, weightCoefFemale: parseFloat(e.target.value) || 0 })); }}
                inputProps={{ step: 0.01 }}
              />
              <TextField
                size="small"
                label="Boy katsayısı"
                type="number"
                value={editCoeffs.heightCoefFemale}
                onChange={(e) => { setSelectedPresetInDialog(null); setEditCoeffs((c) => ({ ...c, heightCoefFemale: parseFloat(e.target.value) || 0 })); }}
                inputProps={{ step: 0.01 }}
              />
              <TextField
                size="small"
                label="Yaş katsayısı"
                type="number"
                value={editCoeffs.ageCoefFemale}
                onChange={(e) => { setSelectedPresetInDialog(null); setEditCoeffs((c) => ({ ...c, ageCoefFemale: parseFloat(e.target.value) || 0 })); }}
                inputProps={{ step: 0.01 }}
              />
              <TextField
                size="small"
                label="Sabit"
                type="number"
                value={editCoeffs.constantFemale}
                onChange={(e) => { setSelectedPresetInDialog(null); setEditCoeffs((c) => ({ ...c, constantFemale: parseFloat(e.target.value) || 0 })); }}
                inputProps={{ step: 0.01 }}
              />
            </Box>
          </Paper>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <CancelButton onClick={() => setFormulaEditDialogOpen(false)} size="small" />
        <Button variant="contained" onClick={saveFormulaEdit} sx={{ bgcolor: colors.primary.main, '&:hover': { bgcolor: colors.hover.primary } }} size="small">
          Uygula
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default AddClientModal;
